import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Megaphone } from "lucide-react";
import { api } from "@/lib/api";

// Different update tags get different highlight colors so urgent ones
// (New/Alert) pop instead of every tag looking the same.
const TAG_STYLES = {
  new: { pill: "bg-white text-red-600", dot: "bg-red-500", blink: true },
  alert: { pill: "bg-white text-red-600", dot: "bg-red-500", blink: true },
  default: { pill: "bg-white text-emerald2-700", dot: "bg-emerald2-500", blink: false },
};

const getTagStyle = (date) => TAG_STYLES[date?.toLowerCase()] || TAG_STYLES.default;

// "Latest Updates" news ticker pinned at the very top, right under the fixed
// navbar (so it's in the first view). Content is managed from the admin
// dashboard's Content tab (backed by /api/content/updates).
export const LatestUpdates = () => {
  const [updates, setUpdates] = useState([]);

  useEffect(() => {
    api
      .listUpdates()
      .then(({ updates }) => setUpdates(updates.map((u) => ({ date: u.tag, text: u.text }))))
      .catch(() => {});
  }, []);

  if (updates.length === 0) return null;

  return (
    <motion.section
    className="relative z-10 bg-navy-50 pt-[68px]"
    data-testid="latest-updates"
    aria-label="Latest updates"
    initial={{ y: -60, opacity: 0 }}
    animate={{ y: 0, opacity: 1 }}
    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
  >
    <div className="relative flex items-stretch overflow-hidden bg-navy-50/70 shadow-[0_2px_6px_rgba(9,19,39,0.12)]">
      {/* Fixed label */}
      <div className="relative flex shrink-0 items-center gap-2 bg-navy-900 px-3.5 py-3 text-white sm:px-5">
        <span>
          <Megaphone className="h-4 w-4 text-gold-400" />
        </span>
        <motion.span
          className="relative flex items-center gap-1.5 rounded-full bg-gold-500 px-2.5 py-1 text-navy-950"
          animate={{ opacity: [1, 0.4, 1] }}
          transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
        >
          <span className="h-1.5 w-1.5 rounded-full bg-navy-950" />
          <span className="text-[10px] font-extrabold uppercase tracking-[0.14em]">Live</span>
        </motion.span>
      </div>

      {/* Scrolling ticker */}
      <div className="relative flex flex-1 items-center overflow-hidden bg-[#062350c4]">
        <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-10 bg-gradient-to-r from-sky-200 to-transparent sm:w-16" />
        <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-10 bg-gradient-to-l from-sky-200 to-transparent sm:w-16" />
        <Marquee gradient={false} speed={45} autoFill pauseOnHover className="mt-1 w-full py-3">
          {updates.map((u, i) => {
            const tag = getTagStyle(u.date);
            return (
              <div key={i} className="flex items-center gap-3 px-8" data-testid={`update-item-${i}`}>
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-gold-500 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-gold-500" />
                </span>
                {u.date ? (
                  <motion.span
                    className={`flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ${tag.pill}`}
                    animate={tag.blink ? { opacity: [1, 0.35, 1], scale: [1, 1.06, 1] } : {}}
                    transition={{ duration: 0.9, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <span className={`h-1.5 w-1.5 rounded-full ${tag.dot}`} />
                    {u.date}
                  </motion.span>
                ) : null}
                <span className="text-sm font-semibold text-white">{u.text}</span>
              </div>
            );
          })}
        </Marquee>
      </div>
    </div>
  </motion.section>
  );
};
